import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { FileItem } from '@aposin/ng-aquila/file-uploader';
import { FORMFIELD_DEFAULT_OPTIONS, FormfieldDefaultOptions, NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NX_MODAL_DATA } from '@aposin/ng-aquila/modal';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { TranslateModule } from '@ngx-translate/core';

import { DocumentHandler } from '#components/DocumentHandler';
import { FileUploaderComponent } from '#components/file-uploader/file-uploader.component';
import { ESG_GERL_QUIZ_COMMENT_TEMPLATE, esgGerlQuizCommentChangedValidator } from '#data/esg-gerl-quiz-comment';
import CveService from '#services/cve/cve.service';
import { formatDate } from '#utils/dateUtils';

const formfieldDefaultOptions: FormfieldDefaultOptions = {
  appearance: 'outline',
  nxFloatLabel: 'always',
};

export interface ESGGerlEscalationModalData {
  controllerI18n: string;
  escalateMicroController: (comment?: string, documents?: FileItem[]) => Promise<void>;
  closeModal: () => void;
  i18n?: string;
  setShouldClose: (val: boolean) => void;
}

@Component({
  template: `<form class="max-h-[90vh]" data-testid="esg-gerl-escalation-modal" [formGroup]="escalateForm">
    <h3 [id]="headingId" nxHeadline class="nx-font-weight-semibold" size="m">
      @if (data.i18n) {
        {{ data.i18n | translate }}
      } @else {
        @if (data.controllerI18n | translate; as controlName) {
          {{ 'escalate.modal.title' | translate: { controlName } }}
        }
      }
    </h3>
    <div class="mb-2">
      <span class="nx-font-weight-semibold">{{ 'escalate.modal.date' | translate }}</span> {{ todaysDate }}
    </div>
    <nx-formfield [label]="'escalate.modal.esg-gerl.quiz-comment' | translate">
      <textarea nxInput rows="12" spellcheck="false" formControlName="esgGerlQuizComment" type="text"></textarea>
    </nx-formfield>
    <nx-formfield [label]="'escalate.modal.comment-optional' | translate">
      <textarea nxInput type="text" name="comment" formControlName="comment"></textarea>
    </nx-formfield>
    <kyc-file-uploader
      [showOptional]="true"
      (filesChange)="onFilesChange($event)"
      (fileDeletion)="onFileDeletion($event)"
    />
    <div class="flex justify-center mt-7">
      <button [disabled]="escalateForm.invalid || loading" (click)="submit()" nxButton="small-medium" type="button">
        @if (loading) {
          <nx-spinner />
        } @else {
          {{ 'escalate.modal.button.submit' | translate }}
        }
      </button>
    </div>
    <div class="flex justify-center mt-3.5">
      <button [disabled]="loading" (click)="data.closeModal()" nxButton="tertiary small-medium" type="button">
        {{ 'escalate.modal.button.dismiss' | translate }}
      </button>
    </div>
  </form>`,
  imports: [
    NxHeadlineModule,
    NxFormfieldModule,
    FormsModule,
    NxInputModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxRadioToggleModule,
    NxRadioModule,
    FileUploaderComponent,
    NxSpinnerModule,
    TranslateModule,
  ],
  providers: [{ provide: FORMFIELD_DEFAULT_OPTIONS, useValue: formfieldDefaultOptions }],
})
export class ESGGerlEscalationModalComponent extends DocumentHandler {
  static readonly headingId = 'esg-gerl-escalation-modal-heading';
  protected readonly headingId = ESGGerlEscalationModalComponent.headingId;

  readonly data = inject<ESGGerlEscalationModalData>(NX_MODAL_DATA);
  private readonly fb = inject(FormBuilder);

  private readonly cveService = inject(CveService);

  private readonly iterationSignal = toSignal(this.cveService.iterationData$);

  protected readonly escalateForm = this.fb.group({
    comment: [''],
    esgGerlQuizComment: [ESG_GERL_QUIZ_COMMENT_TEMPLATE, [Validators.required, esgGerlQuizCommentChangedValidator]],
  });
  protected loading = false;
  constructor() {
    super();

    const iterationData = this.iterationSignal();
    if (!iterationData) {
      throw new Error('No iteration data');
    }

    if (iterationData.customProperties.esgGerlQuizComment) {
      this.escalateForm.patchValue({ esgGerlQuizComment: iterationData.customProperties.esgGerlQuizComment as string });
    }
  }

  protected async submit() {
    const iterationData = this.iterationSignal();
    if (!iterationData) {
      throw new Error('No iteration data');
    }

    this.loading = true;
    this.data.setShouldClose(false);

    await this.cveService.patchIteration(iterationData.iterationId, {
      customProperties: { esgGerlQuizComment: this.escalateForm.value.esgGerlQuizComment },
    });
    await this.data.escalateMicroController(this.escalateForm.value.comment ?? undefined, this.documents ?? []);

    this.loading = false;
    this.data.setShouldClose(true);
    this.data.closeModal();
  }

  protected get todaysDate() {
    return formatDate(new Date().toString());
  }
}
